import React, { useEffect, useMemo, useState } from "react";
import PageWrapper from "../../components/PageWrapper/PageWrapper";
import venueApiService from "../../services/venueApi";
import venueDashboardApiService from "../../services/venueDashboardApi";
import { getVenueHidePriceForAdmin } from "../../utils/venuePricing";
import { clearVenueRecordCache } from "../../utils/venueRecordCache";
import { clearVenueDiscoveryCache } from "../../utils/venueDiscoveryCache";

const hidePriceFromVenue = (venue) => getVenueHidePriceForAdmin(venue);

const AdminVenuePricing = () => {
  const [venues, setVenues] = useState([]);
  const [draftById, setDraftById] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refreshTick, setRefreshTick] = useState(0);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");
  const [saveError, setSaveError] = useState("");

  useEffect(() => {
    let cancelled = false;

    const loadVenues = async () => {
      try {
        setLoading(true);
        setError("");
        setSaveMessage("");
        setSaveError("");
        const allVenues = await venueApiService.getAllVenues({ forceRefresh: true });
        if (!cancelled) {
          const sorted = [...(Array.isArray(allVenues) ? allVenues : [])].sort((a, b) =>
            String(a?.name || "").localeCompare(String(b?.name || ""))
          );
          setVenues(sorted);
          setDraftById({});
        }
      } catch (e) {
        if (!cancelled) {
          setError(e?.message || "Failed to load venues.");
          setVenues([]);
          setDraftById({});
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadVenues();
    return () => {
      cancelled = true;
    };
  }, [refreshTick]);

  const savedById = useMemo(() => {
    const map = {};
    venues.forEach((venue) => {
      const id = String(venue?.id || "");
      if (!id) return;
      map[id] = { hidePrice: hidePriceFromVenue(venue) };
    });
    return map;
  }, [venues]);

  const displayById = useMemo(() => {
    const map = {};
    Object.entries(savedById).forEach(([id, saved]) => {
      const draft = draftById[id] || {};
      map[id] = {
        hidePrice: draft.hidePrice ?? saved.hidePrice
      };
    });
    return map;
  }, [savedById, draftById]);

  const dirtyVenueIds = useMemo(
    () =>
      Object.keys(displayById).filter((id) => {
        const saved = savedById[id];
        const display = displayById[id];
        if (!saved || !display) return false;
        return display.hidePrice !== saved.hidePrice;
      }),
    [displayById, savedById]
  );

  const hasUnsavedChanges = dirtyVenueIds.length > 0;

  const handlePricingChoice = (venueId, hidePrice) => {
    const venueIdKey = String(venueId || "");
    if (!venueIdKey) return;

    setSaveMessage("");
    setSaveError("");
    setDraftById((prev) => {
      const saved = savedById[venueIdKey];
      if (!saved) return prev;

      const nextHide = Boolean(hidePrice);
      if (nextHide === saved.hidePrice) {
        const copy = { ...prev };
        delete copy[venueIdKey];
        return copy;
      }

      return {
        ...prev,
        [venueIdKey]: { hidePrice: nextHide }
      };
    });
  };

  const handleDiscardChanges = () => {
    setDraftById({});
    setSaveMessage("");
    setSaveError("");
  };

  const handleSaveChanges = async () => {
    if (!hasUnsavedChanges || saving) return;

    setSaving(true);
    setSaveMessage("");
    setSaveError("");

    const updates = dirtyVenueIds.map((venueIdKey) => ({
      venueIdKey,
      hidePrice: displayById[venueIdKey].hidePrice
    }));

    try {
      const results = await Promise.allSettled(
        updates.map(({ venueIdKey, hidePrice }) =>
          venueDashboardApiService
            .updateVenueDetails(venueIdKey, { hide_price: hidePrice })
            .then((updated) => ({ venueIdKey, hidePrice, updated }))
        )
      );

      const failed = results
        .map((result, index) => ({ result, ...updates[index] }))
        .filter(({ result }) => result.status === "rejected");

      if (failed.length > 0) {
        const failedNames = failed
          .map(({ venueIdKey }) => venues.find((v) => String(v.id) === venueIdKey)?.name || venueIdKey)
          .join(", ");
        throw new Error(`Failed to save: ${failedNames}`);
      }

      const mismatched = results
        .filter((result) => result.status === "fulfilled")
        .map((result) => result.value)
        .filter(({ hidePrice, updated }) => {
          const fromApi =
            updated?.hide_price != null
              ? Boolean(updated.hide_price)
              : hidePriceFromVenue(updated);
          return fromApi !== hidePrice;
        });

      if (mismatched.length > 0) {
        throw new Error(
          "The server did not confirm your changes. Please try again shortly."
        );
      }

      dirtyVenueIds.forEach((venueIdKey) => {
        clearVenueRecordCache(venueIdKey);
        clearVenueDiscoveryCache(venueIdKey);
      });
      clearVenueRecordCache();
      clearVenueDiscoveryCache();

      const allVenues = await venueApiService.getAllVenues({ forceRefresh: true });
      const sorted = [...(Array.isArray(allVenues) ? allVenues : [])].sort((a, b) =>
        String(a?.name || "").localeCompare(String(b?.name || ""))
      );
      setVenues(sorted);
      setDraftById({});
      setSaveMessage(
        updates.length === 1
          ? "Saved 1 venue."
          : `Saved ${updates.length} venues.`
      );
    } catch (e) {
      setSaveError(e?.message || "Could not save changes. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageWrapper useNewFooter useLanding2Header>
      <main className="bg-white">
        <div className="mx-auto w-full max-w-[96rem] px-6 py-10 sm:px-8">
          <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="text-[11px] font-medium uppercase tracking-[0.2em] text-gray-500">
                Admin
              </p>
              <h1 className="font-heading mt-2 text-3xl font-bold text-gray-900">
                Public pricing visibility
              </h1>
              <p className="mt-2 text-sm text-gray-500">
                Route: <code>/admin/venues/pricing</code>
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {hasUnsavedChanges ? (
                <button
                  type="button"
                  onClick={handleDiscardChanges}
                  disabled={saving}
                  className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Discard
                </button>
              ) : null}
              <button
                type="button"
                onClick={handleSaveChanges}
                disabled={!hasUnsavedChanges || saving || loading}
                className="rounded-lg bg-[#046242] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#035238] focus:outline-none focus:ring-2 focus:ring-[#046242]/40 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {saving ? "Saving..." : "Save changes"}
              </button>
              <button
                type="button"
                onClick={() => setRefreshTick((prev) => prev + 1)}
                disabled={saving}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Refresh
              </button>
            </div>
          </div>

          {hasUnsavedChanges ? (
            <p className="mb-4 text-sm text-amber-700">
              You have unsaved changes for {dirtyVenueIds.length} venue
              {dirtyVenueIds.length === 1 ? "" : "s"}. Click Save changes to persist.
            </p>
          ) : null}
          {saveMessage ? <p className="mb-4 text-sm text-green-700">{saveMessage}</p> : null}
          {saveError ? <p className="mb-4 text-sm text-red-600">{saveError}</p> : null}

          <section className="rounded-xl border border-gray-200 bg-white p-4 sm:p-5">
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Pricing on the public site</h2>
              <p className="mt-1 text-sm text-gray-600">
                Choose whether each venue shows rates on search cards, the booking calendar, and the
                booking form. Hidden venues use “contact for pricing” instead.
              </p>
            </div>
            {loading ? <p className="text-sm text-gray-600">Loading venues...</p> : null}
            {error ? <p className="text-sm text-red-600">{error}</p> : null}
            {!loading && !error && venues.length === 0 ? (
              <p className="text-sm text-gray-600">No venues found.</p>
            ) : null}
            {!loading && !error && venues.length > 0 ? (
              <div className="overflow-hidden rounded-lg border border-gray-200">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Venue
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Venue ID
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Public pricing
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Pending
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                      {venues.map((venue) => {
                        const venueIdKey = String(venue?.id || "");
                        const display = displayById[venueIdKey] || {
                          hidePrice: hidePriceFromVenue(venue)
                        };
                        const isDirty = dirtyVenueIds.includes(venueIdKey);
                        const showPrices = !display.hidePrice;
                        return (
                          <tr key={venueIdKey || venue?.name}>
                            <td className="px-4 py-3 text-sm text-gray-800">
                              <div className="font-medium text-gray-900">{venue?.name || "Untitled venue"}</div>
                              <div className="text-xs text-gray-500">{venue?.city || "—"}</div>
                            </td>
                            <td className="px-4 py-3 text-xs text-gray-600">{venueIdKey || "—"}</td>
                            <td className="px-4 py-3 text-sm text-gray-700">
                              <fieldset className="flex flex-col gap-2 sm:flex-row sm:gap-6">
                                <legend className="sr-only">Public pricing for {venue?.name}</legend>
                                <label className="inline-flex cursor-pointer items-center gap-2">
                                  <input
                                    type="radio"
                                    name={`pricing-${venueIdKey}`}
                                    checked={showPrices}
                                    disabled={saving || !venueIdKey}
                                    onChange={() => handlePricingChoice(venueIdKey, false)}
                                  />
                                  <span>Show prices</span>
                                </label>
                                <label className="inline-flex cursor-pointer items-center gap-2">
                                  <input
                                    type="radio"
                                    name={`pricing-${venueIdKey}`}
                                    checked={!showPrices}
                                    disabled={saving || !venueIdKey}
                                    onChange={() => handlePricingChoice(venueIdKey, true)}
                                  />
                                  <span>Hide prices</span>
                                </label>
                              </fieldset>
                            </td>
                            <td className="px-4 py-3 text-xs">
                              {isDirty ? (
                                <span className="font-medium text-amber-700">Unsaved</span>
                              ) : (
                                <span className="text-gray-500">—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}
          </section>
        </div>
      </main>
    </PageWrapper>
  );
};

export default AdminVenuePricing;
